import * as React from 'react'
import { Layers } from 'lucide-react'

import { ChevronDown } from '@/components/Icons'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui'
import { useDocumentStore } from '@/features/document'
import { cn } from '@/utils'

import { useTflViewerStore } from '../../store'
import { DocumentGroup } from '../../utils/searchResultGrouping'

const VERSION_NUMBER_DECIMAL_PLACES = 1

type TSearchResultItemProps = {
  documentGroup: DocumentGroup
  groupIndex: number
}

/**
 * Component for displaying a document group with all its versions
 */
const SearchResultItem: React.FC<TSearchResultItemProps> = ({
  documentGroup,
  groupIndex,
}) => {
  // Only subscribe to specific fields needed by this component
  const {
    openDocuments,
    focusSearchResult,
    setOpenDocument,
    setActiveDocumentId,
    setFocusSearchResult,
  } = useDocumentStore()
  const metadataByDocId = useTflViewerStore((state) => state.metadataByDocId)

  const [isOpen, setIsOpen] = React.useState<boolean>(true)

  const matchesResult = React.useMemo(() => {
    return `${documentGroup.totalMatches} ${documentGroup.totalMatches === 1 ? 'result' : 'results'} in ${
      documentGroup.versionCount
    }/${documentGroup.totalVersions} ${documentGroup.versionCount === 1 ? 'version' : 'versions'}`
  }, [
    documentGroup.totalMatches,
    documentGroup.totalVersions,
    documentGroup.versionCount,
  ])

  const isDisabled = React.useMemo(
    () => !documentGroup.totalMatches,
    [documentGroup.totalMatches]
  )

  const document = React.useMemo(() => {
    return metadataByDocId[documentGroup.documentId]
  }, [metadataByDocId, documentGroup.documentId])

  const isDocumentOpening = React.useMemo(() => {
    return openDocuments.some((doc) => doc.id === documentGroup.documentId)
  }, [openDocuments, documentGroup.documentId])

  const handleOpenDocument = React.useCallback(
    (documentId: string) => {
      if (!documentId || !document) return

      if (!isDocumentOpening) {
        setOpenDocument([
          ...openDocuments,
          { id: documentId, filename: document.displayName || '' },
        ])
      }

      setActiveDocumentId(documentId)
    },
    [
      document,
      isDocumentOpening,
      openDocuments,
      setActiveDocumentId,
      setOpenDocument,
    ]
  )

  const handleHighlightClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      const displayName = e.currentTarget.dataset.displayName
      const documentId = e.currentTarget.dataset.documentId
      const versionId = e.currentTarget.dataset.versionId
      const index = e.currentTarget.dataset.index
      const keywords = e.currentTarget.dataset.keywords

      if (!documentId || !versionId || !document) return

      setFocusSearchResult({
        displayName: displayName || '',
        documentId,
        versionId,
        index: index ? Number(index) : 0,
        keywords: keywords ? keywords.split(',') : [],
      })
    },
    [document, setFocusSearchResult]
  )

  const handleDocumentClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      const documentId = e.currentTarget.dataset.documentId
      if (!documentId || !document) return

      handleOpenDocument(documentId)
    },
    [document, handleOpenDocument]
  )

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={isDisabled ? undefined : setIsOpen}
      className={cn(
        'py-0.5',
        groupIndex === 0
          ? ''
          : 'border-t border-r-0 border-solid border-secondary'
      )}
    >
      {/* Document Header */}
      <CollapsibleTrigger
        asChild
        className="w-full"
        aria-label={isOpen ? 'Collapse document' : 'Expand document'}
      >
        <button
          type="button"
          className="border-none bg-primary cursor-pointer py-2.5 px-lg text-sm"
          onClick={isDisabled ? handleDocumentClick : undefined}
          data-document-id={documentGroup.documentId}
        >
          <div className="flex gap-4 justify-between w-full">
            <div className="flex-1 flex gap-1 font-medium">
              <div className="text-quaternary-500 min-w-5 flex-shrink-0">{`${groupIndex + 1}.`}</div>
              <div
                className="text-primary-900 flex-1 text-left break-all"
                dangerouslySetInnerHTML={{ __html: documentGroup.displayName }}
              />
            </div>
            {isDisabled ? null : (
              <div className="size-5 flex-shrink-0">
                <ChevronDown
                  size={20}
                  className={cn(
                    'size-5 transition-transform duration-200 text-foreground-fg-quaternary-400',
                    isOpen ? 'rotate-180' : ''
                  )}
                />
              </div>
            )}
          </div>
          {isDisabled ? null : (
            <div className="pl-6 pr-5 text-left text-tertiary-600">
              {matchesResult}
            </div>
          )}
        </button>
      </CollapsibleTrigger>

      {/* Versions List */}
      <CollapsibleContent className="pl-3 pb-4 space-y-2">
        {documentGroup.versions.map((version, documentIndex) => {
          if (!version.matchCount) return null

          return (
            <div key={`${version.versionId}-${documentIndex}`}>
              <div className="flex gap-1 items-center pl-2xl pr-3 pt-xxs pb-2.5 text-sm">
                <Layers
                  size={16}
                  className="size-4 text-foreground-fg-quaternary-400 flex-[0_0_16px]"
                />
                <div className="flex justify-between flex-[1_1_auto]">
                  <span className="text-primary-900 font-medium">
                    {version.versionNumber
                      ? `Version ${version.versionNumber.toFixed(VERSION_NUMBER_DECIMAL_PLACES)}`
                      : '-'}
                  </span>
                  <span className="text-tertiary-600">{`Results: ${version.matchCount}`}</span>
                </div>
              </div>
              <div className="pl-9 pr-2 flex flex-col space-y-lg">
                {version.highlights.map((highlight, index) => {
                  const isFocusSearchResult =
                    focusSearchResult?.documentId ===
                      documentGroup.documentId &&
                    focusSearchResult?.versionId === version.versionId &&
                    focusSearchResult?.index === index

                  return (
                    <div
                      key={`${version.versionId}-${index}`}
                      className="relative"
                    >
                      <button
                        className="text-sm px-1 rounded-sm bg-transparent border-none text-left cursor-pointer hover:bg-gray-50"
                        dangerouslySetInnerHTML={{ __html: highlight }}
                        data-display-name={documentGroup.displayName}
                        data-document-id={documentGroup.documentId}
                        data-version-id={version.versionId}
                        data-index={index}
                        data-keywords={documentGroup.keywords.join(',')}
                        onClick={handleHighlightClick}
                      />
                      {isFocusSearchResult && (
                        <div className="absolute top-1 -left-3 rounded-full size-2 bg-foreground-fg-brand-primary-600" />
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </CollapsibleContent>
    </Collapsible>
  )
}

const SearchResultItemMemorized = React.memo(SearchResultItem)
SearchResultItemMemorized.displayName = 'SearchResultItem'

export { SearchResultItemMemorized as SearchResultItem }
